import React from 'react';
import { CSVLink } from 'react-csv';

import { JOURNEY_TEAMS_CSV_IMPORT_BLOCKING_FILE_NAME } from '@/features/csvImport/admin/constants/journeyTeamsCsvImport';
import { useJourneyTeamsCsvImportBuckets } from '@/features/csvImport/admin/hooks/useJourneyTeamsCsvImportBuckets';
import { CsvImportReview } from '@/features/csvImport/admin/organisms/CsvImportReview';
import { LearningJourneyTeamImportPreview } from '@/store/v2/journeys/teamImport';
import { Button } from '@/ui/Button';

const BLOCKING_CSV_HEADERS = [
  { label: 'Email', key: 'email' },
  { label: 'Team', key: 'teamName' },
  { label: 'Reason', key: 'reason' },
];

type JourneyTeamsCsvImportReviewProps = {
  fileName: string;
  preview: LearningJourneyTeamImportPreview;
  onUploadDifferentFile: VoidFunction;
};

/**
 * The review step of populating a journey's learning teams from a file: the shared review screen
 * fed by this flow's buckets, plus the download of everything the admin has to fix first.
 *
 * The download is hidden outright when nothing blocks set-up, rather than offered as an empty
 * file — which is also the moment the Set up button becomes available.
 */
export const JourneyTeamsCsvImportReview = ({
  fileName,
  preview,
  onUploadDifferentFile,
}: JourneyTeamsCsvImportReviewProps): JSX.Element => {
  const { buckets, blockingRows } = useJourneyTeamsCsvImportBuckets(preview);

  return (
    <CsvImportReview
      fileName={fileName}
      buckets={buckets}
      // Every section starts closed here: the counts are the answer, and the tables are only
      // there for the admin who wants to check one.
      expandFirstBucket={false}
      onUploadDifferentFile={onUploadDifferentFile}
      summaryAction={
        blockingRows.length > 0 && (
          <Button
            variant="link"
            as={CSVLink}
            data={blockingRows.map(({ email, teamName, reason }) => ({ email, teamName, reason }))}
            headers={BLOCKING_CSV_HEADERS}
            filename={JOURNEY_TEAMS_CSV_IMPORT_BLOCKING_FILE_NAME}
          >
            Download errors and missing teams
          </Button>
        )
      }
    />
  );
};
